import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { organizationService } from "../services/organization.service";
import type {
	CreateOrganizationRequest,
	ListOrganizationsParams,
	UpdateOrganizationRequest,
} from "../types";

export const ORGANIZATION_QUERY_KEY = "organizations";
const DEFAULT_LIST_QUERY: ListOrganizationsParams = { page: 1, limit: 10 };

// ─── useOrganizations (list) ─────────────────────────────────────────
export function useOrganizations(params?: Partial<ListOrganizationsParams>) {
	const query = { ...DEFAULT_LIST_QUERY, ...params };
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "list", query],
		queryFn: () => organizationService.list(query),
	});
}

// ─── useOrganization (getById) ────────────────────────────────────────
export function useOrganization(id?: string) {
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "detail", id],
		queryFn: () => organizationService.getById(id as string),
		enabled: !!id,
	});
}

// ─── useChildOrganizations (quyi tashkilotlar) ───────────────────────
export function useChildOrganizations(
	parentId: string,
	params?: { page?: number; limit?: number }
) {
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "children", parentId, params],
		queryFn: () => organizationService.listChildren(parentId, params),
		enabled: !!parentId,
		staleTime: 30_000, // 30s — use prefetched data when opening tab
	});
}

// ─── useMyChildOrganizations (boshqa tashkilotlar) ───────────────────
export function useMyChildOrganizations(params?: {
	page?: number;
	limit?: number;
	search?: string;
	isEducational?: boolean;
}) {
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "my-children", params],
		queryFn: () => organizationService.listMyChildren(params),
		staleTime: 30_000,
	});
}

// ─── useSchoolSearch (nom bo'yicha avlod maktablarni qidirish) ────────
export function useSchoolSearch(search: string, params?: { page?: number; limit?: number }) {
	const trimmed = search.trim();
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "school-search", trimmed, params],
		queryFn: () =>
			organizationService.listMyChildren({ ...params, search: trimmed, isEducational: true }),
		enabled: trimmed.length >= 2,
		staleTime: 30_000,
	});
}

// ─── useOrgCameraStats (kartalar uchun NVR/kamera statistikasi) ───────
export function useOrgCameraStats(orgIds: string[]) {
	const sortedIds = [...orgIds].sort();
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "camera-stats", sortedIds],
		queryFn: () => organizationService.getCameraStats(orgIds),
		enabled: orgIds.length > 0,
		staleTime: 30_000,
	});
}

// ─── useOrganizationTree (tashkilot daraxti) ─────────────────────────
export function useOrganizationTree(rootId?: string) {
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "tree", rootId ?? "default"],
		queryFn: () => organizationService.getTree(rootId),
		staleTime: 5 * 60 * 1000, // 5 daqiqa cache
	});
}

// ─── useOrganizationChildren (childrenlarni lazy loading uchun) ───────
export function useOrganizationChildren(parentId?: string) {
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "tree-children", parentId],
		// biome-ignore lint/style/noNonNullAssertion: parentId is guaranteed by enabled check
		queryFn: () => organizationService.getTreeChildren(parentId!),
		enabled: !!parentId,
		staleTime: 5 * 60 * 1000, // 5 daqiqa cache
	});
}

// ─── useCreateOrganization ────────────────────────────────────────────
export function useCreateOrganization() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateOrganizationRequest) => organizationService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [ORGANIZATION_QUERY_KEY] });
			message.success("Tashkilot yaratildi");
		},
	});
}

// ─── useUpdateOrganization ────────────────────────────────────────────
export function useUpdateOrganization() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationRequest }) =>
			organizationService.update(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [ORGANIZATION_QUERY_KEY] });
			message.success("Tashkilot yangilandi");
		},
	});
}

// ─── useDeleteOrganization ────────────────────────────────────────────
export function useDeleteOrganization() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => organizationService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [ORGANIZATION_QUERY_KEY] });
			message.success("Tashkilot o'chirildi");
		},
	});
}

// ─── useOrganizationNames (breadcrumb uchun id → nom) ─────────────────
/**
 * Bir nechta tashkilot nomini id bo'yicha oladi.
 *
 * `school-cameras` drill-down yo'li URL da faqat ID lar bilan saqlanadi
 * (havola ulashiladigan va refresh'ga chidamli bo'lishi uchun), breadcrumb esa
 * nomlarni talab qiladi. Har bir so'rov `useOrganization` bilan bir xil kalitda
 * keshlanadi — daraxt bo'ylab yurganda takroriy so'rov ketmaydi.
 */
export function useOrganizationNames(ids: string[]) {
	return useQueries({
		queries: ids.map((id) => ({
			queryKey: [ORGANIZATION_QUERY_KEY, "detail", id],
			queryFn: () => organizationService.getById(id),
			staleTime: 5 * 60 * 1000,
		})),
		combine: (results) => ({
			names: results.map((r) => r.data?.name),
			isLoading: results.some((r) => r.isLoading),
		}),
	});
}
